import { api } from "@/services/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { ActivityStatus } from "@/services/api/cronos/activities/update"
import { useActivitiesStore } from "@/modules/study/stores/use-activities-store"
import { toast } from "sonner"
import type { Activity } from "../entities/activities"

interface UpdateActivityData {
  id: string
  status?: ActivityStatus
  dateTime?: string
}

export default function useUpdateActivity() {
  const { setActivities, activities } = useActivitiesStore()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: ["update-activity"],
    mutationFn: ({ id, ...data }: UpdateActivityData) => {
      return api.cronos.activities.update(id, data)
    },
    onMutate: async ({ id, status, dateTime }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["study-get-activities"] })
      await queryClient.cancelQueries({ queryKey: ["get-daily-calendar"] })

      // Snapshot the previous value
      const previousActivities = activities

      // Optimistically update to the new value
      const updatedActivities = activities.map((day) => {
        // Find the activity in this day
        const activityIndex = day.activities.findIndex((activity) => activity.id === id)

        if (activityIndex !== -1) {
          // Make a copy of the activities array
          const updatedDayActivities = [...day.activities]

          // If we are moving to a new date, remove from current day
          if (dateTime && dateTime !== updatedDayActivities[activityIndex].dateTime) {
            // Remove the activity from this day
            updatedDayActivities.splice(activityIndex, 1)
            return {
              ...day,
              activities: updatedDayActivities,
              totalSeconds:
                day.totalSeconds - parseInt(day.activities[activityIndex].estimatedDuration || "0"),
            }
          }
          // Otherwise, update the activity in place
          else if (status) {
            updatedDayActivities[activityIndex] = {
              ...updatedDayActivities[activityIndex],
              status,
            }
            return {
              ...day,
              activities: updatedDayActivities,
            }
          }
        }

        // If this is the target day for a moved activity and we have a dateTime
        if (dateTime && day.date === dateTime) {
          // Find the activity in any day
          const movedActivity = previousActivities
            .flatMap((d) => d.activities)
            .find((a) => a.id === id)

          if (movedActivity) {
            // Create updated activity with new status and dateTime
            const updatedActivity: Activity = {
              ...movedActivity,
              dateTime,
              status:
                status || movedActivity.status === "rescheduled" ? "pending" : movedActivity.status,
            }

            return {
              ...day,
              activities: [...day.activities, updatedActivity],
              totalSeconds: day.totalSeconds + parseInt(updatedActivity.estimatedDuration || "0"),
            }
          }
        }

        return day
      })

      setActivities(updatedActivities)

      return { previousActivities }
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousActivities) {
        setActivities(context.previousActivities)
        toast.error("Error al actualizar la actividad")
      }
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["get-daily-calendar"],
      })
    },
  })

  return mutation
}
